"use client";

import { useEffect, useRef, useState } from "react";
import type { CSSProperties } from "react";

import { Link } from "@/components/ui/link/link";
import { cn } from "@/lib/utils";

import { GradientText, PillCta, SIGNUP_HREF } from "./shared";
import { TIMELINE_SNAP_TARGETS, TIMELINE_STEPS } from "./timeline-data";

const IMG = "/images/wayflyer-intelligence";
const STICKY_HEIGHT = 480;
const STICKY_TOP_OFFSET = 112;
const EASING = "cubic-bezier(0.22, 1, 0.36, 1)";

/** Right-angle bracket at one corner of the card: one dark arm running
 * along the edge toward the card's interior, one dropping/rising into the
 * card — same shape top and bottom, just mirrored vertically. */
function CornerBracket({
  vSide,
  hSide,
}: {
  vSide: "top" | "bottom";
  hSide: "left" | "right";
}) {
  const v = vSide === "top" ? "top-0" : "bottom-0";
  const h = hSide === "left" ? "left-0" : "right-0";
  return (
    <div className={cn("absolute h-2 w-2", v, h)} aria-hidden="true">
      <div className={cn("absolute h-2 w-px bg-[#0d0d0d]", v, h)} />
      <div className={cn("absolute h-px w-2 bg-[#0d0d0d]", v, h)} />
    </div>
  );
}

function CornerMarks() {
  return (
    <>
      <CornerBracket vSide="top" hSide="left" />
      <CornerBracket vSide="top" hSide="right" />
      <CornerBracket vSide="bottom" hSide="left" />
      <CornerBracket vSide="bottom" hSide="right" />
    </>
  );
}

/** Sticky-pinned 5-step scroll story, followed by a plain (non-pinned)
 * closing CTA. Wheel/trackpad input is
 * intercepted while the track is pinned: each scroll gesture advances (or
 * reverses) exactly one step, then input is locked out for a short cooldown
 * so a single trackpad swipe — which fires many wheel events as it decays —
 * can't skip multiple steps or fight its own momentum. Keyboard and
 * scrollbar-drag scrolling (which don't fire wheel events) fall back to the
 * plain continuous scroll-position mapping below, so they still work, just
 * without the one-gesture-one-step pagination. */
export function TimelineSection() {
  const trackRef = useRef<HTMLDivElement>(null);
  const [active, setActive] = useState(0);
  const lockedRef = useRef(false);
  const lockTimerRef = useRef<number | undefined>(undefined);

  useEffect(() => {
    function getProgress() {
      const track = trackRef.current;
      if (!track) return null;
      const r = track.getBoundingClientRect();
      const total = r.height - STICKY_HEIGHT;
      return { progress: (STICKY_TOP_OFFSET - r.top) / total, total, r };
    }

    function computeActive() {
      const p = getProgress();
      if (!p) return;
      const clamped = Math.min(1, Math.max(0, p.progress));
      // Nearest snap target, same approach onWheel already uses below —
      // adapts automatically to however many targets exist instead of a
      // hardcoded bucket width/cap that silently stops working once a
      // target (the closing CTA's) is added past the original 5 steps.
      let next = 0;
      let bestDist = Infinity;
      TIMELINE_SNAP_TARGETS.forEach((t, i) => {
        const d = Math.abs(t - clamped);
        if (d < bestDist) {
          bestDist = d;
          next = i;
        }
      });
      setActive((prev) => (prev !== next ? next : prev));
    }

    function scrollToStop(idx: number) {
      const p = getProgress();
      if (!p) return;
      const trackTop = window.scrollY + p.r.top;
      const target = TIMELINE_SNAP_TARGETS[idx]!;
      const targetY = trackTop - STICKY_TOP_OFFSET + target * p.total;
      window.scrollTo({ top: targetY, behavior: "smooth" });
    }

    function onWheel(e: WheelEvent) {
      const p = getProgress();
      if (!p) return;
      // Only take over once the track is actually pinned — leave normal
      // scrolling untouched everywhere else on the page.
      const engaged = p.progress > -0.02 && p.progress < 1.02;
      if (!engaged) return;

      if (lockedRef.current) {
        // Swallow the rest of this gesture's momentum so it can't queue up
        // additional steps once we unlock.
        e.preventDefault();
        return;
      }

      const dir = e.deltaY > 4 ? 1 : e.deltaY < -4 ? -1 : 0;
      if (dir === 0) return;

      let idx = 0;
      let bestDist = Infinity;
      TIMELINE_SNAP_TARGETS.forEach((t, i) => {
        const d = Math.abs(t - p.progress);
        if (d < bestDist) {
          bestDist = d;
          idx = i;
        }
      });

      const nextIdx = idx + dir;
      // At either end: release control so the section scrolls normally
      // into/out of view instead of trapping the user inside it.
      if (nextIdx < 0 || nextIdx > TIMELINE_SNAP_TARGETS.length - 1) return;

      e.preventDefault();
      setActive(nextIdx);
      scrollToStop(nextIdx);
      lockedRef.current = true;
      window.clearTimeout(lockTimerRef.current);
      lockTimerRef.current = window.setTimeout(() => {
        lockedRef.current = false;
      }, 650);
    }

    window.addEventListener("wheel", onWheel, { passive: false });
    window.addEventListener("scroll", computeActive, { passive: true });
    window.addEventListener("resize", computeActive);
    const initialTimer = window.setTimeout(computeActive, 100);
    return () => {
      window.removeEventListener("wheel", onWheel);
      window.removeEventListener("scroll", computeActive);
      window.removeEventListener("resize", computeActive);
      window.clearTimeout(lockTimerRef.current);
      window.clearTimeout(initialTimer);
    };
  }, []);

  function stepStyle(i: number): CSSProperties {
    const isOn = i === active;
    const isNext = i === active + 1;
    const y = isOn ? 0 : isNext ? 380 : i > active ? 440 : -48;
    return {
      opacity: isOn ? 1 : isNext ? 0.25 : 0,
      transform: `translateY(${y}px)`,
      pointerEvents: isOn ? "auto" : "none",
      transition: `opacity 0.5s ${EASING}, transform 0.5s ${EASING}`,
    };
  }

  function dotStyle(i: number): CSSProperties {
    const isOn = i === active;
    return {
      left: isOn ? "567px" : "570px",
      width: isOn ? "18px" : "12px",
      height: isOn ? "18px" : "12px",
      background: i <= active ? TIMELINE_STEPS[i]!.dotColor : "#e6e6e6",
      boxShadow: isOn ? "0 0 0 6px rgba(176,51,224,0.15)" : "none",
      top: `${30 + i * 90}px`,
    };
  }

  return (
    <>
      {/* Desktop: pixel-accurate sticky scroll story */}
      {/* The 1280px pinned track plus this section's 160px of px-20 padding
          needs exactly 1440px of viewport — gated on `xl` (1440px), not
          `lg` (1056px), so narrower viewports get the stacked layout below
          instead of horizontal overflow. */}
      <section className="hidden border-t border-[#e6e6e6] bg-[#fafaf7] px-20 pb-20 xl:block">
        <div className="relative mx-auto w-[1280px] border border-t-0 border-[#e6e6e6] bg-white p-16 pb-0">
          <CornerMarks />
          <h2
            aria-hidden="true"
            className="invisible m-0 text-center text-[32px] leading-[1.2] font-normal tracking-[-0.04em]"
          >
            Ask anything. Turn your data into your next move.
          </h2>
          <div ref={trackRef} className="relative mt-8 h-[2280px]">
            <div className="sticky top-[112px] h-[480px]">
              <h2 className="absolute inset-x-0 -top-[72px] m-0 text-center text-[32px] leading-[1.2] font-normal tracking-[-0.04em] text-[#0d0d0d]">
                <GradientText>Ask anything.</GradientText> Turn your data into
                your next move.
              </h2>

              {/* Ends at the last dot's center (top-[390px] + ~half its
                  size), not the full sticky height — the line shouldn't
                  run on past the final step. */}
              <div className="absolute top-0 left-[575px] h-[396px] w-[2px] bg-[linear-gradient(180deg,#fe634d_0%,#e842a3_22%,#b033e0_45%,#5e1fff_68%,rgba(0,30,255,0.9)_82%,rgba(0,30,255,0)_100%)]" />

              {TIMELINE_STEPS.map((step, i) => (
                <div
                  key={`dot-${step.title}`}
                  className="absolute rounded-full border-[3px] border-white transition-all duration-400"
                  style={dotStyle(i)}
                />
              ))}

              {TIMELINE_STEPS.map((step, i) => (
                <div
                  key={step.title}
                  className="absolute inset-0"
                  style={stepStyle(i)}
                >
                  <div
                    className={cn(
                      "absolute top-[100px] h-[280px] w-[460px] rounded-2xl border border-[#e6e6e6] bg-gradient-to-br from-[#fafaf7] to-white",
                      step.textSide === "left" ? "left-10" : "right-10"
                    )}
                  >
                    <div
                      className={cn(
                        "absolute left-10 flex w-[400px] flex-col gap-4",
                        i === 0 ? "top-14" : "top-10"
                      )}
                    >
                      <h3 className="m-0 text-[24px] leading-[1.2] font-bold tracking-[-0.04em] text-[#0d0d0d]">
                        {step.title}
                      </h3>
                      <p className="m-0 text-[16px] leading-[1.5] tracking-[-0.02em] text-[#61666b]">
                        {step.blurb}
                      </p>
                      <div className="border-t border-[#e6e6e6] pt-4">
                        <span className="text-[14px] font-bold tracking-[-0.02em] text-[#0d0d0d]">
                          Try asking Intelligence
                        </span>
                        <ul className="mt-3 flex list-disc flex-col gap-2 pl-[18px]">
                          {step.questions.map((q) => (
                            <li
                              key={q}
                              className="text-[14px] leading-[1.5] tracking-[-0.02em] text-[#61666b]"
                            >
                              {q}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </div>
                  <div
                    className={cn(
                      "absolute top-[100px] h-[280px] w-[460px] overflow-hidden rounded-2xl border border-[#e6e6e6]",
                      step.textSide === "left" ? "right-10" : "left-10"
                    )}
                  >
                    <img
                      src={step.media}
                      alt={step.mediaAlt}
                      className={step.mediaClassName}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Desktop closing CTA — a plain static block below the pinned
          timeline box (not part of the scroll-pin sequence), so it always
          sits fully visible with normal spacing instead of needing to be
          squeezed into the sticky viewport alongside "Make the call". */}
      <section className="hidden bg-[#fafaf7] px-20 pt-10 pb-20 xl:block">
        <div
          className="relative mx-auto h-[260px] w-[1280px] overflow-hidden rounded-2xl shadow-[0_27px_62px_rgba(0,30,255,0.18)]"
          style={{
            background:
              "linear-gradient(120deg, #3a6bff 0%, #245eff 35%, #0031f0 100%)",
          }}
        >
          <div
            className="absolute -right-[60px] -bottom-[90px] h-[220px] w-[280px] rounded-full bg-[#cc61cc] opacity-70 blur-[55px]"
            aria-hidden="true"
          />
          <div
            className="absolute -top-[60px] -left-[50px] h-[180px] w-[220px] rounded-full bg-[#5487fa] opacity-70 mix-blend-overlay blur-[50px]"
            aria-hidden="true"
          />
          <img
            src={`${IMG}/intelligence-hero.png`}
            alt="Wayflyer Intelligence UI"
            className="absolute -top-[17px] left-[530px] w-[763px]"
          />
          <div className="absolute top-1/2 left-8 flex w-[440px] -translate-y-1/2 flex-col gap-6">
            <span className="text-[36px] leading-[1.2] tracking-[-0.04em] text-white">
              Apply for financing with Wayflyer and access Intelligence for free
            </span>
            <Link
              href={SIGNUP_HREF}
              external
              className="inline-flex h-9 w-fit items-center rounded-full bg-white px-5 text-[13px] font-medium text-[#0d0d0d] no-underline shadow-[0_2px_8px_rgba(0,10,80,0.25)]"
            >
              Apply now
            </Link>
          </div>
        </div>
      </section>

      {/* Mobile/tablet: same content, plain stacked flow (no scroll-pin) */}
      <section className="border-t border-[#e6e6e6] bg-[#fafaf7] px-5 py-16 xl:hidden">
        <div className="relative flex flex-col gap-10 rounded-2xl border border-[#e6e6e6] bg-white p-6">
          <CornerMarks />
          <h2 className="m-0 text-center text-[28px] leading-[1.2] font-normal tracking-[-0.04em] text-[#0d0d0d]">
            <GradientText>Ask anything.</GradientText> Turn your data into your
            next move.
          </h2>
          {TIMELINE_STEPS.map((step, i) => (
            <div
              key={step.title}
              className="flex flex-col gap-4 rounded-2xl border border-[#e6e6e6] bg-gradient-to-br from-[#fafaf7] to-white p-5"
            >
              <span
                className="flex size-7 items-center justify-center rounded-full text-[12px] font-medium text-white"
                style={{ background: step.dotColor }}
              >
                {i + 1}
              </span>
              <h3 className="m-0 text-[24px] leading-[1.2] font-bold tracking-[-0.04em] text-[#0d0d0d]">
                {step.title}
              </h3>
              <p className="m-0 text-[16px] leading-[1.5] tracking-[-0.02em] text-[#61666b]">
                {step.blurb}
              </p>
              <div className="overflow-hidden rounded-xl border border-[#e6e6e6]">
                <img
                  src={step.media}
                  alt={step.mediaAlt}
                  className="h-auto w-full"
                />
              </div>
              <div className="border-t border-[#e6e6e6] pt-4">
                <span className="text-[14px] font-bold tracking-[-0.02em] text-[#0d0d0d]">
                  Try asking Intelligence
                </span>
                <ul className="mt-3 flex list-disc flex-col gap-2 pl-[18px]">
                  {step.questions.map((q) => (
                    <li
                      key={q}
                      className="text-[14px] leading-[1.5] tracking-[-0.02em] text-[#61666b]"
                    >
                      {q}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          ))}

          <div
            className="relative overflow-hidden rounded-2xl p-6"
            style={{
              background:
                "linear-gradient(120deg, #3a6bff 0%, #245eff 35%, #0031f0 100%)",
            }}
          >
            <div
              className="absolute -right-[60px] -bottom-[90px] h-[220px] w-[280px] rounded-full bg-[#cc61cc] opacity-70 blur-[55px]"
              aria-hidden="true"
            />
            <div
              className="absolute -top-[60px] -left-[50px] h-[180px] w-[220px] rounded-full bg-[#5487fa] opacity-70 mix-blend-overlay blur-[50px]"
              aria-hidden="true"
            />
            <div className="relative flex flex-col gap-6">
              <span className="text-[28px] leading-[1.2] tracking-[-0.04em] text-white">
                Create your Wayflyer account to access Intelligence
              </span>
              <PillCta href={SIGNUP_HREF} inverse className="self-start">
                Sign up
              </PillCta>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}
